import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayeredPet from './LayeredPet';
import { useVirtualPet } from './hooks/useVirtualPet';
import styles from './MyHomePet.module.css';

type PanelMode = 'main' | 'snack' | 'play';

interface MyHomePetProps {
  editing: boolean;
}

const MyHomePet: React.FC<MyHomePetProps> = ({ editing }) => {
  const navigate = useNavigate();
  const pet = useVirtualPet();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PanelMode>('main');

  if (pet.loading || !pet.classSettings.enabled || !pet.profile || !pet.inventory) return null;

  const close = () => {
    setOpen(false);
    setMode('main');
  };

  const run = async (action: () => Promise<void>) => {
    await action();
    setMode('main');
  };

  return (
    <div className={styles.petArea} data-editing={editing || undefined}>
      {pet.message && !open && <div className={styles.message}>{pet.message}</div>}
      {open && !editing && (
        <div className={styles.panel} role="dialog" aria-label={`${pet.profile.name} 돌보기`}>
          <div className={styles.panelHeader}>
            <strong>{mode === 'snack' ? '간식 고르기' : mode === 'play' ? '놀아주기' : pet.profile.name}</strong>
            <button type="button" onClick={close} aria-label="돌보기 메뉴 닫기">×</button>
          </div>

          {mode === 'main' && (
            <div className={styles.actionGrid}>
              <button type="button" onClick={() => setMode('snack')}>🍪<span>간식</span></button>
              <button type="button" onClick={() => void run(() => pet.actions.performPlayAction('stroke'))}>🤚<span>쓰다듬기</span></button>
              <button type="button" onClick={() => setMode('play')}>⚽<span>놀기</span></button>
              <button type="button" onClick={() => void run(() => pet.actions.interact('rest'))}>🌙<span>재우기</span></button>
              <button type="button" className={styles.infoButton} onClick={() => navigate('/class/student/VirtualPet')}>
                📊<span>성장·모험 보기</span>
              </button>
            </div>
          )}

          {mode === 'snack' && (
            <div className={styles.optionList}>
              {pet.ownedPetConsumables.length > 0 ? pet.ownedPetConsumables.map((item) => (
                <button key={item.uniqueKey} type="button" onClick={() => void run(() => pet.actions.useConsumable(item.uniqueKey))}>
                  <span>{item.image ? <img src={item.image} alt="" /> : '🍪'}</span>
                  <strong>{item.name}</strong>
                </button>
              )) : <p>보유한 간식이 없어요.<br />학급 상점에서 구매할 수 있어요.</p>}
              <button type="button" className={styles.backButton} onClick={() => setMode('main')}>뒤로</button>
            </div>
          )}

          {mode === 'play' && (
            <div className={styles.optionList}>
              {pet.playOptions.map((option) => {
                const status = pet.playActionStatus[option.id];
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={!status.canPlay}
                    onClick={() => void run(() => pet.actions.performPlayAction(option.id))}
                  >
                    <span>{option.emoji}</span>
                    <strong>{option.name}</strong>
                    <small>{option.id === 'stroke' ? `무료 ${status.freeRemaining || 0}회` : `이용권 ${status.ticketCount}개`}</small>
                  </button>
                );
              })}
              <button type="button" className={styles.backButton} onClick={() => setMode('main')}>뒤로</button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className={styles.petButton}
        onClick={() => { if (!editing) setOpen((value) => !value); }}
        disabled={editing}
        aria-label={`${pet.profile.name} 돌보기`}
        aria-expanded={open}
      >
        <LayeredPet
          size={64}
          equipped={pet.inventory.equipped}
          petForm={pet.profile.petForm}
          petName={pet.profile.name}
          isEating={pet.isEating}
          isPlaying={pet.isPlaying}
          isSleeping={pet.isSleeping}
          isEvolving={pet.isEvolving}
          isReadyToEvolve={Boolean(pet.evolutionStatus?.satisfied)}
        />
      </button>
    </div>
  );
};

export default MyHomePet;
